import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Button,
  Card,
  Center,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { toApiError } from '../apollo/errors';
import { useAuth } from '../auth/context';
import { AdminLoginDocument } from '../graphql/operations';

// The same rules the API checks, so an obvious mistake does not cost a round
// trip -- and the same wording, so the two never contradict each other.
export const EMAIL_MESSAGE_KEY = 'login.errors.email';
export const PASSWORD_MESSAGE_KEY = 'login.errors.password';

export function LoginPage() {
  const { token, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [login, { loading, error }] = useMutation(AdminLoginDocument);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : t(EMAIL_MESSAGE_KEY)),
      password: (value) => (value.length >= 8 ? null : t(PASSWORD_MESSAGE_KEY)),
    },
  });

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (token) return <Navigate to={from} replace />;

  const apiError = toApiError(error);

  const submit = form.onSubmit(async (values) => {
    try {
      const result = await login({
        variables: { input: { email: values.email.trim(), password: values.password } },
      });

      const accessToken = result.data?.adminLogin.accessToken;

      if (!accessToken) return;

      signIn(accessToken);
      await navigate(from, { replace: true });
    } catch (thrown) {
      // The headline is already in `error` from the hook; what is worth doing
      // here is putting the API's per-field messages under the right inputs.
      const failure = toApiError(thrown as ErrorLike);

      if (failure && Object.keys(failure.fields).length > 0) form.setErrors(failure.fields);
    }
  });

  return (
    <Center h="100vh" px="md">
      <Card withBorder shadow="sm" padding="xl" w={380}>
        <form onSubmit={(event) => void submit(event)} noValidate>
          <Stack gap="md">
            <div>
              <Title order={3}>talumi</Title>
              <Text size="sm" c="dimmed">
                {t('login.subtitle')}
              </Text>
            </div>

            {apiError && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {apiError.message}
              </Alert>
            )}

            <TextInput
              label={t('login.email')}
              placeholder={t('login.emailPlaceholder')}
              type="email"
              autoComplete="username"
              key={form.key('email')}
              {...form.getInputProps('email')}
            />

            <PasswordInput
              label={t('login.password')}
              autoComplete="current-password"
              key={form.key('password')}
              {...form.getInputProps('password')}
            />

            <Button type="submit" loading={loading} fullWidth>
              {t('login.submit')}
            </Button>
          </Stack>
        </form>
      </Card>
    </Center>
  );
}
